import { Users, User, CheckCircle2, AlertCircle, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";

interface GroupVsIndividualComparisonProps {
  groupScore: number;
  groupDollarImpact: number;
  groupZone: string;
  individualTotal: number;
  eligibleClinicianCount: number;
  ineligibleClinicianCount: number;
  recommendation: 'GROUP' | 'INDIVIDUAL' | 'EITHER';
  differenceAmount: number;
  hasEligibilityData: boolean;
  className?: string;
}

function formatCurrency(amount: number): string {
  const absAmount = Math.abs(amount);
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(absAmount);
  
  if (amount < 0) return '-' + formatted;
  if (amount > 0) return '+' + formatted;
  return formatted;
}

export function GroupVsIndividualComparison({
  groupScore,
  groupDollarImpact,
  groupZone,
  individualTotal,
  eligibleClinicianCount,
  ineligibleClinicianCount,
  recommendation,
  differenceAmount,
  hasEligibilityData,
  className
}: GroupVsIndividualComparisonProps) {
  const isGroupBetter = recommendation === 'GROUP';
  const isIndividualBetter = recommendation === 'INDIVIDUAL';
  const isEither = recommendation === 'EITHER';

  const groupIsPenalty = groupDollarImpact < 0;
  const groupIsBonus = groupDollarImpact > 0;
  const individualIsPenalty = individualTotal < 0;
  const individualIsBonus = individualTotal > 0;

  return (
    <div className={cn("w-full", className)}>
      <h3 className="text-sm font-medium mb-3">Reporting Pathway Comparison</h3>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Group Reporting Card */}
        <div className={cn(
          "rounded-xl border-2 p-4 transition-all",
          isGroupBetter ? "border-primary bg-primary/5" : "border-muted"
        )}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              </div>
              <span className="font-medium">Group Reporting</span>
            </div>
            {isGroupBetter && (
              <Badge variant="default" className="bg-primary">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Recommended
              </Badge>
            )}
          </div>
          
          <div className="space-y-2">
            <div className={cn(
              "text-3xl font-bold",
              groupIsPenalty ? "text-red-600 dark:text-red-400" :
              groupIsBonus ? "text-emerald-600 dark:text-emerald-400" :
              "text-muted-foreground"
            )}>
              {formatCurrency(groupDollarImpact)}
            </div>
            <p className="text-sm text-muted-foreground">
              Based on TIN score of {Math.round(groupScore)}
            </p>
            <p className="text-xs text-muted-foreground">
              Same adjustment applies to all eligible clinicians
            </p>
          </div>
        </div>

        {/* Individual Reporting Card */}
        <div className={cn(
          "rounded-xl border-2 p-4 transition-all",
          isIndividualBetter ? "border-primary bg-primary/5" : "border-muted"
        )}>
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                <User className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="font-medium">Individual Reporting</span>
            </div>
            {isIndividualBetter && (
              <Badge variant="default" className="bg-primary">
                <CheckCircle2 className="h-3 w-3 mr-1" />
                Recommended
              </Badge>
            )}
          </div>
          
          {hasEligibilityData ? (
            <div className="space-y-2">
              <div className={cn(
                "text-3xl font-bold",
                individualIsPenalty ? "text-red-600 dark:text-red-400" :
                individualIsBonus ? "text-emerald-600 dark:text-emerald-400" :
                "text-muted-foreground"
              )}>
                {formatCurrency(individualTotal)}
              </div>
              <p className="text-sm text-muted-foreground">
                Sum of {eligibleClinicianCount} eligible clinician adjustments
              </p>
              {ineligibleClinicianCount > 0 && (
                <p className="text-xs text-muted-foreground">
                  {ineligibleClinicianCount} clinician{ineligibleClinicianCount > 1 ? 's' : ''} not MIPS eligible
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Data needed</span>
              </div>
              <p className="text-xs text-muted-foreground">
                Upload the clinician eligibility file in Configuration → QPP Reports to enable individual reporting comparison.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Comparison Result */}
      {hasEligibilityData && (
        <div className={cn(
          "mt-4 p-4 rounded-lg flex items-center justify-between",
          isGroupBetter ? "bg-blue-50 dark:bg-blue-900/20" :
          isIndividualBetter ? "bg-purple-50 dark:bg-purple-900/20" :
          "bg-muted"
        )}>
          <div className="flex items-center gap-3">
            <ArrowRight className={cn(
              "h-5 w-5",
              isGroupBetter ? "text-blue-600 dark:text-blue-400" :
              isIndividualBetter ? "text-purple-600 dark:text-purple-400" :
              "text-muted-foreground"
            )} />
            <div>
              <p className="font-medium">
                {isEither ? (
                  "Both pathways yield similar results"
                ) : (
                  <>
                    {recommendation.charAt(0) + recommendation.slice(1).toLowerCase()} reporting saves{' '}
                    <span className="text-emerald-600 dark:text-emerald-400">
                      {formatCurrency(Math.abs(differenceAmount))}
                    </span>{' '}
                    more
                  </>
                )}
              </p>
              {!isEither && (
                <p className="text-sm text-muted-foreground">
                  Difference between the two pathways
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

